// ID Photo Typesetting Engine

export interface PaperSize {
  name: string;
  widthMm: number;
  heightMm: number;
}

export const PAPERS: Record<string, PaperSize> = {
  '4r': { name: '6 Inches / 4R', widthMm: 101.6, heightMm: 152.4 }, // 4" x 6"
  '3r': { name: '5 Inches / 3R', widthMm: 88.9, heightMm: 127.0 },  // 3.5" x 5"
  'a4': { name: 'A4', widthMm: 210.0, heightMm: 297.0 }
};

export interface LayoutOptions {
  paperSize: string;           // '4r' | '3r' | 'a4'
  orientation: 'landscape' | 'portrait';
  marginMm: number;            // Page margin
  gapMm: number;               // Spacing between photos
  showCutLines: boolean;       // Draw cutting guides
  showBorder: boolean;         // Draw thin border around photo
  quantity: number;            // 0 means auto-fill max
  dpi?: number;                // DPI, defaults to 300
}

/**
 * Converts millimeters to pixels at a given DPI
 */
export function mmToPx(mm: number, dpi: number = 300): number {
  return Math.round((mm / 25.4) * dpi);
}

/**
 * Calculates the typesetting grid details
 */
export function calculateLayout(
  photoWidthMm: number,
  photoHeightMm: number,
  options: LayoutOptions
) {
  const dpi = options.dpi || 300;
  const paper = PAPERS[options.paperSize] || PAPERS['4r'];
  
  // Set paper dimensions based on orientation
  let paperWidthMm = paper.widthMm;
  let paperHeightMm = paper.heightMm;
  if (options.orientation === 'landscape') {
    paperWidthMm = paper.heightMm;
    paperHeightMm = paper.widthMm;
  }

  const paperW = mmToPx(paperWidthMm, dpi);
  const paperH = mmToPx(paperHeightMm, dpi);
  const margin = mmToPx(options.marginMm, dpi);
  const gap = mmToPx(options.gapMm, dpi);

  const printableW = paperW - 2 * margin;
  const printableH = paperH - 2 * margin;

  const photoW = mmToPx(photoWidthMm, dpi);
  const photoH = mmToPx(photoHeightMm, dpi);

  // Check orientation of individual photos to fit max possible
  // Standard layout (upright)
  const colsStd = Math.floor((printableW + gap) / (photoW + gap));
  const rowsStd = Math.floor((printableH + gap) / (photoH + gap));
  const maxStd = Math.max(0, colsStd * rowsStd);

  // Rotated layout (photos rotated 90 deg)
  const colsRot = Math.floor((printableW + gap) / (photoH + gap));
  const rowsRot = Math.floor((printableH + gap) / (photoW + gap));
  const maxRot = Math.max(0, colsRot * rowsRot);

  // Decide if we should rotate individual photos on sheet
  const useRotated = maxRot > maxStd;
  const maxFit = useRotated ? maxRot : maxStd;
  const cols = useRotated ? colsRot : colsStd;
  const rows = useRotated ? rowsRot : rowsStd;
  
  const w = useRotated ? photoH : photoW;
  const h = useRotated ? photoW : photoH;

  // Quantity to draw
  const actualQty = options.quantity <= 0 ? maxFit : Math.min(options.quantity, maxFit);

  // Grid arrangement details
  const activeRows = Math.ceil(actualQty / cols);
  const activeCols = Math.min(actualQty, cols);

  // Total dimensions of the grid
  const gridW = activeCols * w + (activeCols - 1) * gap;
  const gridH = activeRows * h + (activeRows - 1) * gap;

  // Center the grid in the printable area
  const startX = margin + (printableW - gridW) / 2;
  const startY = margin + (printableH - gridH) / 2;

  return {
    paperWidthPx: paperW,
    paperHeightPx: paperH,
    photoWidthPx: photoW,
    photoHeightPx: photoH,
    gapPx: gap,
    cols,
    rows,
    useRotated,
    actualQty,
    startX,
    startY,
    drawW: w,
    drawH: h,
    maxFit
  };
}

/**
 * Renders the typeset print sheet onto a canvas
 */
export function renderPrintSheet(
  croppedCanvas: HTMLCanvasElement,
  photoWidthMm: number,
  photoHeightMm: number,
  options: LayoutOptions,
  targetCanvas?: HTMLCanvasElement
): HTMLCanvasElement {
  const layout = calculateLayout(photoWidthMm, photoHeightMm, options);
  
  const canvas = targetCanvas || document.createElement('canvas');
  canvas.width = layout.paperWidthPx;
  canvas.height = layout.paperHeightPx;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // 1. Draw solid white background for printing
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // 2. Draw photos
  let count = 0;
  for (let r = 0; r < layout.rows && count < layout.actualQty; r++) {
    for (let c = 0; c < layout.cols && count < layout.actualQty; c++) {
      const x = layout.startX + c * (layout.drawW + layout.gapPx);
      const y = layout.startY + r * (layout.drawH + layout.gapPx);

      ctx.save();
      ctx.translate(x + layout.drawW / 2, y + layout.drawH / 2);
      
      if (layout.useRotated) {
        ctx.rotate(Math.PI / 2);
      }

      // Draw the cropped photo
      ctx.drawImage(
        croppedCanvas,
        -layout.photoWidthPx / 2,
        -layout.photoHeightPx / 2,
        layout.photoWidthPx,
        layout.photoHeightPx
      );
      ctx.restore();

      // Draw thin photo border if requested
      if (options.showBorder) {
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, layout.drawW, layout.drawH);
      }

      // Draw dashed cutting lines in the middle of the gap spacing
      if (options.showCutLines) {
        ctx.save();
        ctx.strokeStyle = '#c0c0c0';
        ctx.lineWidth = 2; // 300 DPI needs a bit thicker lines
        ctx.setLineDash([6, 6]);
        
        const halfGap = layout.gapPx / 2;
        // If gap is 0, draw exactly on the borders. Otherwise draw in between.
        const offset = halfGap > 0 ? halfGap : 0;
        
        ctx.strokeRect(
          x - offset,
          y - offset,
          layout.drawW + (offset * 2),
          layout.drawH + (offset * 2)
        );
        ctx.restore();
      }

      count++;
    }
  }

  return canvas;
}
